// AdminTreatments.jsx
import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import AdminLayout from "../../../../layouts/admin/AdminLayout";
import AppContext from "../../../../context/AppContext";
import "../../../../assets/css/admin/dashboardcss/AdminTreatments.css";
import axios from "axios";
import { checkLoggedInAdmin } from "../../../../helper/CustomHelper";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import EsTreatmentCard from "../../../../components/customer/Treatments/EsTreatmentCard";

export default function Treatments() {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);
  const navigate = useNavigate();

  // STATES
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [price, setPrice] = useState("");
  const [image, setImage] = useState(null);

  const [treatments, setTreatments] = useState([]);
  const [categories, setCategories] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTreatment, setEditTreatment] = useState(null);

  // ✅ Loading states
  const [isOpeningAdd, setIsOpeningAdd] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

 // CREATE TREATMENT
const createTreatment = () => {
  if (
    !name.trim() ||
    !category ||
    !tagline.trim() ||
    !description.trim() ||
    !duration ||
    !price ||
    !image
  ) {
    Swal.fire({
      icon: "warning",
      title: "Missing Information",
      text: "Please fill in all fields and select an image before saving.",
      confirmButtonColor: "#e0456e",
    });
    return;
  }
    setIsSaving(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("category", category);
    formData.append("tagline", tagline);
    formData.append("description", description);
    formData.append("duration", duration);
    formData.append("price", price);
    formData.append("file", image);

    axios
      .post(`${baseUrl}/admin/treatments/createTreatment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        console.log(response.data);

        Swal.fire({
          icon: "success",
          title: "Treatment Added!",
          text: "Treatment has been added successfully.",
          confirmButtonColor: "#e0456e",
        });

        viewAllTreatments();
        setShowAddModal(false);
        resetForm();
      })
      .catch((error) => {
        console.log(error);

        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: error.response?.data?.message || "Unable to add treatment.",
          confirmButtonColor: "#e0456e",
        });
      })
      .finally(() => setIsSaving(false));
  };

  // UPDATE TREATMENT
  const updateTreatment = () => {
    setIsSaving(true);

    const formData = new FormData();
    formData.append("id", editTreatment.id);
    formData.append("name", name);
    formData.append("category", category);
    formData.append("tagline", tagline);
    formData.append("description", description);
    formData.append("duration", duration);
    formData.append("price", price);
    formData.append("file", image);

    axios
      .post(`${baseUrl}/admin/treatments/updateTreatment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        console.log(response.data);

        Swal.fire({
          icon: "success",
          title: "Treatment Updated!",
          text: "Treatment has been updated successfully.",
          confirmButtonColor: "#e0456e",
        });

        viewAllTreatments();
        setShowEditModal(false);
        resetForm();
      })
      .catch((error) => {
        console.log(error);

        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: error.response?.data?.message || "Unable to update treatment.",
          confirmButtonColor: "#e0456e",
        });
      })
      .finally(() => setIsSaving(false));
  };

  // VIEW ALL TREATMENTS
  const viewAllTreatments = () => {
    axios
      .get(`${baseUrl}/admin/treatments/viewAllTreatments`)
      .then((response) => {
        console.log(response.data);
        setTreatments(response.data.treatments || []);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // VIEW ALL CATEGORIES (for the select dropdown)
  const viewAllCategories = () => {
    axios
      .get(`${baseUrl}/admin/category/viewAllCategories`)
      .then((response) => {
        console.log(response.data);
        setCategories(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // DELETE TREATMENT
  const deleteTreatment = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This treatment will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e0456e",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post(`${baseUrl}/admin/treatments/deleteTreatment`, { id: id })
          .then((response) => {
            console.log(response.data);

            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Treatment has been deleted successfully.",
              confirmButtonColor: "#e0456e",
            });

            viewAllTreatments();
          })
          .catch((error) => {
            console.log(error);

            Swal.fire({
              icon: "error",
              title: "Failed!",
              text:
                error.response?.data?.message ||
                "Unable to delete treatment.",
              confirmButtonColor: "#e0456e",
            });
          });
      }
    });
  };

  const resetForm = () => {
    setName("");
    setCategory("");
    setTagline("");
    setDescription("");
    setDuration("");
    setPrice("");
    setImage(null);
  };

  // PAGE LOAD
  useEffect(() => {
    checkLoggedInAdmin(navigate);
    viewAllTreatments();
    viewAllCategories();
  }, []);

  // OPEN ADD MODAL (with loading)
  const openAddModal = () => {
    setIsOpeningAdd(true);

    setTimeout(() => {
      resetForm();
      setShowAddModal(true);
      setIsOpeningAdd(false);
    }, 300);
  };

  return (
    <AdminLayout>
      <div className="treatments-page">
        <Container fluid className="px-4 px-md-5">
          {/* HEADER */}
          <Row className="align-items-center page-header">
            <Col>
              <h1 className="page-title">Services</h1>
              <p className="page-subtitle">Manage your treatment menu.</p>
            </Col>

            <Col xs="auto">
              <button
                className="btn-add-category"
                onClick={openAddModal}
                disabled={isOpeningAdd}
              >
                <span className="plus-icon">+</span>
                {isOpeningAdd ? "Opening..." : "Add Service"}
              </button>
            </Col>
          </Row>

          {/* CARDS — same EsTreatmentCard used on the customer site.
              Plain CSS grid (no Bootstrap Row/Col nesting) so the
              Bootstrap column inside EsTreatmentCard doesn't fight
              with an outer Bootstrap column. */}
          <div className="admin-treatments-grid">
            {treatments.length > 0 ? (
              treatments.map((item) => (
                <div key={item.id} className="admin-treatment-card-wrap">
                  <EsTreatmentCard treatment={item} screenName="admin" />

                  {/* Edit / Delete overlay */}
                  <div className="admin-treatment-overlay">
                    <button
                      onClick={() => {
                        setEditTreatment(item);
                        setName(item.name);
                        setCategory(item.category);
                        setTagline(item.tagline);
                        setDescription(item.description);
                        setDuration(item.duration);
                        setPrice(item.price);
                        setShowEditModal(true);
                      }}
                      style={overlayBtnStyle}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTreatment(item.id)}
                      style={{ ...overlayBtnStyle, color: "#e0456e" }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No treatments available.</p>
            )}
          </div>
        </Container>
      </div>

      {/* ADD MODAL */}
      {showAddModal && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 1040,
            }}
            onClick={() => !isSaving && setShowAddModal(false)}
          />

          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 500,
              background: "#fff",
              borderRadius: 12,
              padding: "32px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h5 style={{ marginBottom: 20, fontWeight: 700 }}>Add Service</h5>

            <div style={{ marginBottom: 16 }}>
              <label>Image</label>
              <input
                type="file"
                style={{ display: "block", marginTop: 6, width: "100%" }}
                onChange={(e) => setImage(e.target.files[0])}
                disabled={isSaving}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Treatment Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isSaving}
                style={inputStyle}
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.title}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Tagline</label>
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                disabled={isSaving}
                placeholder="e.g. Clear Skin Starts Here"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <Row>
              <Col md={6}>
                <div style={{ marginBottom: 16 }}>
                  <label>Duration (min)</label>
                  <input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    disabled={isSaving}
                    style={inputStyle}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: 24 }}>
                  <label>Price</label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    disabled={isSaving}
                    style={inputStyle}
                  />
                </div>
              </Col>
            </Row>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowAddModal(false)}
                disabled={isSaving}
                style={cancelBtnStyle}
              >
                Cancel
              </button>

              <button
                onClick={createTreatment}
                disabled={isSaving}
                style={{
                  ...saveBtnStyle,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* EDIT MODAL */}
      {showEditModal && editTreatment && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 1040,
            }}
            onClick={() => !isSaving && setShowEditModal(false)}
          />

          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 500,
              background: "#fff",
              borderRadius: 12,
              padding: "32px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h5 style={{ marginBottom: 20, fontWeight: 700 }}>Edit Service</h5>

            <div style={{ marginBottom: 16 }}>
              <label>Image (leave empty to keep current)</label>
              <input
                type="file"
                style={{ display: "block", marginTop: 6, width: "100%" }}
                onChange={(e) => setImage(e.target.files[0])}
                disabled={isSaving}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Treatment Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isSaving}
                style={inputStyle}
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.title}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Tagline</label>
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                disabled={isSaving}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label>Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>

            <Row>
              <Col md={6}>
                <div style={{ marginBottom: 16 }}>
                  <label>Duration (min)</label>
                  <input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    disabled={isSaving}
                    style={inputStyle}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: 24 }}>
                  <label>Price</label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    disabled={isSaving}
                    style={inputStyle}
                  />
                </div>
              </Col>
            </Row>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowEditModal(false)}
                disabled={isSaving}
                style={cancelBtnStyle}
              >
                Cancel
              </button>

              <button
                onClick={updateTreatment}
                disabled={isSaving}
                style={{
                  ...saveBtnStyle,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

// Shared inline style objects
const inputStyle = {
  display: "block",
  marginTop: 6,
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #ddd",
  borderRadius: 8,
  fontSize: 14,
};

const cancelBtnStyle = {
  padding: "9px 20px",
  borderRadius: 8,
  border: "1px solid #ddd",
  background: "#fff",
  cursor: "pointer",
  fontSize: 14,
};

const saveBtnStyle = {
  padding: "9px 20px",
  borderRadius: 8,
  border: "none",
  background: "#e0456e",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
};

const overlayBtnStyle = {
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: 6,
  padding: "5px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
};